import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from '../components/constants';
import { authHeaders } from '../utils/authHeaders';

// Async thunk for fetching support settings
export const fetchSupportSettings = createAsyncThunk(
  'supportSettings/fetchSupportSettings',
  async () => {
    const response = await axios.get(`${BASE_URL}/support-settings`, authHeaders());
    return response.data;
  }
);

// Async thunk for updating support settings.
// `information_text`, `feedback_link`, and `support_hours` always go through
// the form. The time/date settings (`timezone`, `date_format`, `time_format`,
// `week_starts_on`) are optional — when omitted the backend leaves the
// existing DB values intact (via COALESCE in the UPDATE).
export const updateSupportSettings = createAsyncThunk(
  'supportSettings/updateSupportSettings',
  async ({
    information_text,
    feedback_link,
    support_hours,
    timezone,
    date_format,
    time_format,
    week_starts_on,
  }) => {
    const formData = new FormData();
    formData.append('information_text', information_text);
    formData.append('feedback_link', feedback_link);
    if (support_hours !== undefined && support_hours !== null) {
      formData.append('support_hours', support_hours);
    }
    if (timezone !== undefined && timezone !== null) formData.append('timezone', timezone);
    if (date_format !== undefined && date_format !== null) formData.append('date_format', date_format);
    if (time_format !== undefined && time_format !== null) formData.append('time_format', time_format);
    if (week_starts_on !== undefined && week_starts_on !== null) formData.append('week_starts_on', week_starts_on);

    const response = await axios.post(`${BASE_URL}/support-settings`, formData, authHeaders());
    return response.data;
  }
);

const supportSettingsSlice = createSlice({
  name: 'supportSettings',
  initialState: {
    information_text: '',
    feedback_link: '',
    support_hours: '',
    timezone: 'UTC',
    date_format: 'MM/DD/YYYY',
    time_format: '12',
    week_starts_on: 'monday',
    loading: false,
    error: null,
    lastUpdated: null,
  },
  reducers: {
    clearSupportSettings: (state) => {
      state.information_text = '';
      state.feedback_link = '';
      state.error = null;
    },
    setInformationText: (state, action) => {
      state.information_text = action.payload;
    },
    setFeedbackLink: (state, action) => {
      state.feedback_link = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupportSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupportSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.information_text = action.payload.information_text || '';
        state.feedback_link = action.payload.feedback_link || '';
        state.support_hours = action.payload.support_hours || '';
        state.timezone = action.payload.timezone || 'UTC';
        state.date_format = action.payload.date_format || 'MM/DD/YYYY';
        state.time_format = action.payload.time_format || '12';
        state.week_starts_on = action.payload.week_starts_on || 'monday';
        state.lastUpdated = action.payload.updated_at;
      })
      .addCase(fetchSupportSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateSupportSettings.pending, (state) => {
        // Note: do NOT toggle the page-level `loading` flag here. Save uses
        // a button-level spinner via the component's local isSaving state,
        // so the rest of the UI stays mounted.
        state.error = null;
      })
      .addCase(updateSupportSettings.fulfilled, (state, action) => {
        state.error = null;
        // Optimistically reflect the values we sent so the page refreshes
        // its content without a follow-up fetch. Only fields actually
        // included in the request payload get overwritten.
        const arg = action.meta?.arg || {};
        if (arg.information_text !== undefined) state.information_text = arg.information_text;
        if (arg.feedback_link !== undefined) state.feedback_link = arg.feedback_link;
        if (arg.support_hours !== undefined) state.support_hours = arg.support_hours;
        if (arg.timezone !== undefined && arg.timezone !== null) state.timezone = arg.timezone;
        if (arg.date_format !== undefined && arg.date_format !== null) state.date_format = arg.date_format;
        if (arg.time_format !== undefined && arg.time_format !== null) state.time_format = arg.time_format;
        if (arg.week_starts_on !== undefined && arg.week_starts_on !== null) state.week_starts_on = arg.week_starts_on;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateSupportSettings.rejected, (state, action) => {
        state.error = action.error.message;
      });
  },
});

export const { clearSupportSettings, setInformationText, setFeedbackLink } = supportSettingsSlice.actions;
export default supportSettingsSlice.reducer;
